"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";

import { Link } from "@/i18n/navigation";
import { OrganizationData } from "@/entities/organization/model/types";
import { withErrorBoundary } from "@/shared/lib/error-boundary";
import { Button } from "@/shared/ui/buttons/button";

import { OrganizationGrid } from "./organization-grid";
import { OrganizationList } from "./organization-list";
import { OrganizationFilters, ViewMode } from "./organization-filters";
import { OrganizationDisplaySkeleton } from "./skeletons/organization-display-skeleton";
import { LOCAL_STORAGE_KEYS } from "@/shared/config";

interface OrganizationDisplayProps {
  organizations: OrganizationData[];
  searchInput: string;
  onSearchChange: (value: string) => void;
  onSearch: (query: string) => void;
  isLoading?: boolean;
  filterBy: string;
  sortBy: string;
  onFilterChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onLoadMore?: (() => void) | undefined;
  hasMore?: boolean | undefined;
}

const VIEW_MODE_STORAGE_KEY = LOCAL_STORAGE_KEYS.ORGANIZATIONS_VIEW_MODE;

function OrganizationDisplay({
  organizations,
  searchInput,
  onSearchChange,
  // onSearch prop is not used in the new design as we use instant search with debounce from parent
  isLoading = false,
  filterBy,
  sortBy,
  onFilterChange,
  onSortChange,
  onLoadMore,
  hasMore,
}: OrganizationDisplayProps) {
  const t = useTranslations("Organizations.page");
  const [displayMode, setDisplayMode] = useState<ViewMode>("grid");

  useEffect(() => {
    const savedMode = localStorage.getItem(VIEW_MODE_STORAGE_KEY) as ViewMode;
    if (savedMode) {
      setDisplayMode(savedMode);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, displayMode);
    }
  }, [displayMode]);

  const renderDisplay = () => {
    if (isLoading && organizations.length === 0) {
      return <OrganizationDisplaySkeleton />;
    }

    if (displayMode === "list") {
      return (
        <OrganizationList
          organizations={organizations}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
        />
      );
    }
    
    return (
      <OrganizationGrid
        organizations={organizations}
        onLoadMore={onLoadMore}
        hasMore={hasMore}
      />
    );
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background py-8">
      <div className="container mx-auto flex flex-col px-2 sm:px-4 lg:px-8">
        <div className="space-y-6 rounded-2xl border border-border/40 bg-black/5 px-4 py-5 shadow-sm backdrop-blur-sm dark:bg-white/5 sm:px-6">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold tracking-tight">
                {t("title")}
              </h1>
            </div>
            <Button
              asChild
              className="transition-transform duration-150 hover:-translate-y-0.5"
            >
              <Link href="/get-started">{t("create")}</Link>
            </Button>
          </div>

          <OrganizationFilters
            searchInput={searchInput}
            onSearchChange={onSearchChange}
            filterBy={filterBy}
            onFilterChange={onFilterChange}
            sortBy={sortBy}
            onSortChange={onSortChange}
            viewMode={displayMode}
            setViewMode={setDisplayMode}
          />

          <div className="rounded-xl border border-border/30 bg-background/60 p-4 shadow-sm transition-colors">
            {renderDisplay()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default withErrorBoundary(OrganizationDisplay);
